import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

import useQuestionnaire from 'hooks/useQuestionnaire';
import { checkSpanishLocale, cn } from 'lib/utils';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { useTranslation } from 'react-i18next';

export default function Allergies() {
  const { t } = useTranslation();
  const questionData = useQuestionnaireStore((store) => store.questionData);
  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const [allergies, setAllergies] = useState<string[]>([]);
  const [newAllergy, setNewAllergy] = useState('');

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const userChoice =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];
      if (userChoice === 'No') {
        setAllergies([]);
        return;
      }

      const answer = userChoice?.split(', ');
      setAllergies(answer?.length ? answer?.map((item) => item.trim()) : []);
    }
  }, [isSpanish, questionData]);

  const onAdd = () => {
    setAllergies([...allergies, newAllergy]);
    setNewAllergy('');
  };

  const onDelete = (allergy: string) => {
    setAllergies(allergies.filter((c) => c !== allergy));
  };

  const onClearAll = () => setAllergies([]);

  const onContinue = async () => {
    let choiceValue = '';
    if (allergies.length > 0) {
      choiceValue = allergies.join(', ');
    }
    onSubmit({
      choice_value: choiceValue || 'No',
      spanish_choice_value: choiceValue || 'No',
      questionData,
    });
  };

  return (
    <QuestionnaireContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={12}
    >
      <div className='flex items-center gap-4'>
        <Input
          className='w-full md:w-1/2 h-9'
          value={newAllergy}
          onChange={(e) => setNewAllergy(e.target.value)}
          placeholder={t('add_allergy')}
        />
        <Button onClick={onAdd} className='px-6 h-9'>
          {t('add')}
        </Button>
      </div>

      <div className='mt-4'>
        <div className='flex items-center justify-between'>
          <h1 className='font-semibold'>{t('allergies_added')}</h1>
          <Button
            variant='link'
            className='text-sm underline transition-all duration-300 hover:no-underline text-rose-500 h-9'
            onClick={onClearAll}
            disabled={!allergies.length}
            size='sm'
          >
            {t('clear_all')}
          </Button>
        </div>
        <div className='flex flex-wrap gap-2'>
          {allergies?.length ? (
            allergies.map((allergy) => {
              return (
                <motion.div
                  key={allergy}
                  className={cn(
                    'border border-gray-300 text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-between cursor-pointer transition-all duration-150 gap-1.5'
                  )}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => onDelete(allergy)}
                >
                  <p className='leading-5'>{allergy}</p>
                  <X className='w-4 h-4' />
                </motion.div>
              );
            })
          ) : (
            <div
              className={cn(
                'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150'
              )}
            >
              <p className='leading-5'>{t('none')}</p>
            </div>
          )}
        </div>
      </div>
    </QuestionnaireContainer>
  );
}
